import React from 'react';
import styled from 'styled-components';
import { Text } from '@amzn/storm-ui';
import {
  accountSelectionListId,
  IA2AccountDropdownSearchInput,
  AccountTypeLabelContainer,
  AccountTypeLabel,
  IA2AccountListItem,
} from './styles';
import { useAccountDropdownSearch } from './useAccountDropdownSearch';
import { IA2AccountDropdownTypeSection } from './IA2AccountDropdownTypeSection';
import { AccountFlatList } from './AccountFlatList';
import { useAccountData } from './useAccountData';
import { IA2AccountDropdownInfoCard } from './IA2AccountDropdownInfoCard';
import { useAccountDropdownContext } from './AccountDropdownProvider';
import { IA2AccountDropdownFooterLinks } from './IA2AccountDropdownFooterLinks';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { getAccountTypeListLabel } from './AccountTypeLocalizationKeyMapping';
import {
  buildAllAccountsUrl,
  findSelectedAccountSwitcherAccount,
} from './accountHelper';
import { ErrorLoadingAccountsMessage } from 'src/features/header/components/AccountDropdown/ErrorLoadingAccountsMessage';
import { NoAccountsMessage } from 'src/features/header/components/AccountDropdown/NoAccountsMessage';
import {
  AdvertisingAccountLink,
  AdvertisingAccountContainer,
} from './IA2AdvertisingAccountItem';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { Flex } from '@amzn/storm-ui';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';

const SEARCH_DISPLAY_COUNT = 10;

const AccountSwitcherContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xsmall.default};
`;

// The infinite-scroll sentinel observes this element, so it MUST be the
// scrollable viewport: flex:1 + min-height:0 lets it shrink below its content
// inside the 100vh flex column overlay (without min-height:0 a flex child keeps
// its content height and never scrolls), and overflow-y:auto makes it the
// scroll container the prefetch observer roots against.
const MobileAccountListContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  position: relative;
  padding: 0 ${({ theme }) => theme.spacing.xsmall.default};
  border-top: ${({ theme }) => theme.border.subtle.default};
`;

const AccountDropdownWithSearchContainer = styled(Flex).attrs({
  direction: 'column',
  alignItems: 'inherit',
})`
  flex: 1;
  min-height: 0;
`;

export const IA2MobileAccountDropdown: React.VFC = () => {
  const {
    accounts,
    accountCounter,
    loadingStatus,
    currentEntityId,
    currentMarketplaceId,
    isPrecomputed,
    totalCount,
    nextToken,
  } = useAccountDropdownContext();

  const bundle = useAccountDropdownBundle();
  const chromeConfig = useChromeConfigContext();
  const isT1Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM,
  );
  const isT2Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM_T2,
  );
  const isTrimNavMigrationEnabled = isT1Enabled || isT2Enabled;
  const supportedPageScopes = chromeConfig.pageHeader?.supportedPageScopes;
  const isSwitchable =
    Array.isArray(supportedPageScopes) && supportedPageScopes.length > 1;
  const isCurrentScopeAllAccounts =
    chromeConfig.pageHeader?.pageScope === 'ALL_ACCOUNTS';
  const isFailedLoadingAccountData = loadingStatus === 'error';
  const listContainerRef = React.useRef<HTMLDivElement>(null);

  // Both data hooks are called unconditionally (rules of hooks); the weblab flag
  // selects which drives rendering. Control = legacy local search + paginated
  // type sections; treatment = unified flat list + dual-mode useAccountData
  // (same UX and server pagination as desktop — mobile is NOT limited to the
  // page-one seed).
  const { onSearch, accountTypeList, onClear, searchValue } =
    useAccountDropdownSearch({
      accounts,
    });
  const dataSource = useAccountData({
    isPrecomputed: isPrecomputed === true,
    seedAccounts: accounts,
    seedTotalCount: totalCount ?? 0,
    seedNextToken: nextToken ?? null,
    seedFailed: isFailedLoadingAccountData,
    currentMarketplaceId,
  });

  // Show search only when useful: more than a page-full of accounts, or more
  // pages exist server-side (nextToken != null). Mirrors the desktop gate.
  const hasMorePages = isTrimNavMigrationEnabled && nextToken != null;
  const isSearchInputVisible =
    accountCounter > SEARCH_DISPLAY_COUNT || hasMorePages;

  const searchAccountPlaceholder = bundle.getMessage(
    'account-dropdown-placeholder',
  );

  const selectedAccount = findSelectedAccountSwitcherAccount(
    currentEntityId,
    accounts.map((accountGroup) => accountGroup.accounts).flat(),
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isTrimNavMigrationEnabled) {
      dataSource.search(e.target.value);
    } else {
      onSearch(e);
    }
  };

  const handleSearchClear = () => {
    if (isTrimNavMigrationEnabled) {
      dataSource.clearSearch();
    } else {
      onClear();
    }
  };

  return (
    <AccountDropdownWithSearchContainer data-ccx-e2e-id="aac-mobile-account-dropdown-wrapper">
      <AccountSwitcherContainer>
        <IA2AccountDropdownInfoCard />
        {isSearchInputVisible && (
          <IA2AccountDropdownSearchInput
            id="account-switcher-search"
            placeholder={searchAccountPlaceholder}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            fullWidth={true}
            label={searchAccountPlaceholder}
            {...(!isTrimNavMigrationEnabled && { value: searchValue })}
          />
        )}
      </AccountSwitcherContainer>
      <MobileAccountListContainer
        id={accountSelectionListId}
        ref={listContainerRef}
      >
        {isSwitchable && (
          <Flex direction="column" alignItems="normal">
            <AccountTypeLabelContainer>
              <AccountTypeLabel weight="bold" withMargin>
                {bundle.getMessage('account-switcher-all-accounts-label')}
              </AccountTypeLabel>
            </AccountTypeLabelContainer>
            <IA2AccountListItem isSelected={isCurrentScopeAllAccounts}>
              {isCurrentScopeAllAccounts ? (
                <AdvertisingAccountContainer>
                  <Text>
                    {bundle.getMessage('account-switcher-all-accounts-label')}
                  </Text>
                </AdvertisingAccountContainer>
              ) : (
                <AdvertisingAccountLink
                  data-ccx-e2e-id="aac-all-accounts-link-mobile"
                  href={buildAllAccountsUrl()}
                  draggable="false"
                  {...getTaktAttributes(
                    'aac-all-accounts-link-mobile',
                    chromeComponents.HEADER,
                  )}
                >
                  <Text>
                    {bundle.getMessage('account-switcher-all-accounts-label')}
                  </Text>
                </AdvertisingAccountLink>
              )}
            </IA2AccountListItem>
          </Flex>
        )}
        {isTrimNavMigrationEnabled ? (
          <AccountFlatList
            dataSource={dataSource}
            selectedEntityId={selectedAccount?.entityId}
            scrollRootRef={listContainerRef}
          />
        ) : (
          <>
            {isFailedLoadingAccountData && <ErrorLoadingAccountsMessage />}
            {!isFailedLoadingAccountData && accountTypeList.length === 0 && (
              <NoAccountsMessage />
            )}
            {!isFailedLoadingAccountData &&
              accountTypeList.length > 0 &&
              accountTypeList.map((accountTypeItem) => {
                return (
                  <IA2AccountDropdownTypeSection
                    key={accountTypeItem.accountType}
                    accounts={accountTypeItem.accounts}
                    label={getAccountTypeListLabel(
                      accountTypeItem.accountType,
                      bundle,
                    )}
                    type={accountTypeItem.accountType}
                  />
                );
              })}
          </>
        )}
      </MobileAccountListContainer>
      <IA2AccountDropdownFooterLinks />
    </AccountDropdownWithSearchContainer>
  );
};
